// =============================================================================
//  Copyright : 2023 semantics GmbH
//  Date      : $Date: 2024-05-07 11:37:01 +0200 (Tue, 07 May 2024) $
//  Author    : Dirk Rothe
//  Worker    : $Author: o.powroznik $
//  Revision  : $Rev: 142605 $
//  Purpose   : viewer for many pages
// =============================================================================
// ---Third-party imports
import React from 'react'
import clsx from 'clsx'

// ---Framework imports
import {isNil, isString} from 'ldash'
import log from 'uix/logging'
import {PageSetViewer} from 'uix/viewer/components'
import {useFullscreen, useInView, useDelay, useState, useSelector} from 'uix/hooks'

// ---Local imports
import {Button, ButtonGroup} from './button'
import S from './pageViewer.scss'
import {useCaptionLink} from '../hooks'

// ---Exports
const useNavigation = ({navigationControls, viewerData, setScrollToPageId, optionSet}) => {
  let [centerSelected, setCenterSelected] = useState(null)
  let onCenterSelectionChange = ({otid}) => {
    //~ log.x('%o', {otid, viewerData})
    setCenterSelected(otid)
  }
  let navigate = change_idx => {
    let idx = viewerData.index?.byId[centerSelected]?.flatIdx
    if (isNil(idx)) return
    let pageIds = viewerData.index?.pageIds || []
    let next_idx = Math.max(0, Math.min(change_idx(idx), pageIds.length - 1))
    let otid = viewerData.index.pageIds.at(next_idx)
    if (otid) {
      setScrollToPageId(otid)
    }
  }
  let delta = {v2: 2, v4: 4}[optionSet] || 1
  let onForwardClick = () => navigate(idx => idx + delta)
  let onBackwardClick = () => navigate(idx => idx - delta)

  return {onCenterSelectionChange, onBackwardClick, onForwardClick}
}

const useViewerControl = () => {
  let [viewerInstance, setViewerInstance] = useState(null)
  let zoomIn = () => {
    viewerInstance.setZoom(viewerInstance.state.zoom * 1.4)
  }
  let zoomOut = () => {
    viewerInstance.setZoom(viewerInstance.state.zoom * 1.4 ** -1)
  }
  return {onViewerInstance: setViewerInstance, zoomIn, zoomOut}
}

const ControlButton = ({icon, onClickFunc}) => {
  return (
    <Button
      size="tiny"
      style={{
        color: 'var(--presentation_themeColor)',
        border: '1px solid var(--presentation_themeColor)',
      }}
      icon={icon}
      onClick={onClickFunc}
    />
  )
}

export const PageViewer = ({
  otid,
  otids,
  linkid,
  caption,
  width,
  mobile,
  height,
  optionSet,
  initialZoomMode,
  initAni,
  initAniWait,
  initAniDuration,
  initAniScale,
  dpiAware,
  gutterX,
  gutterY,
  paginationMode, // null, custom or fluid
  clsHover, // css class for hover selection
  wheelMode,
  wheelModeMeta,
  fullscreenToggle, // show fullscreen button
  navigationControls, // show forward/backward buttons
  zoomControls, // show zoom buttons
  imageClick,
}) => {
  let [inviewRef, inView] = useInView({triggerOnce: false})
  let {ref, toggleFullscreen, isFullscreen} = useFullscreen()
  let options = {
    zoom: {},
    layout: {dpiAware},
    render: {},
  }
  let merge_opt = (options, name, value) => {
    if (isNil(value)) return options // do not inject if null/undefined
    let [n1, n2] = name.split('.')
    return {...options, [n1]: {...options[n1], [n2]: value}}
  }

  options = merge_opt(options, 'zoom.mode', initialZoomMode)
  options = merge_opt(options, 'render.paginations', paginationMode)
  options = merge_opt(options, 'layout.gutter_x', gutterX)
  options = merge_opt(options, 'layout.gutter_y', gutterY)
  options = merge_opt(options, 'mouse.wheelMode', wheelMode)
  options = merge_opt(options, 'mouse.wheelModeMeta', wheelModeMeta)
  options = merge_opt(options, 'render.clsHover', S[clsHover])

  if (isFullscreen) {
    // remove width/height restictions
    ;[width, height] = ['100vw', '100vh']
  }
  let [scrollToPageId, setScrollToPageId] = useState(null)
  useDelay(
    () => {
      if (initAni == 'zoomAll') {
        setScrollToPageId({ids: 'ALL_IDS', duration: initAniDuration * 1000, scale: initAniScale})
      }
    },
    inView ? initAniWait * 1000 || 1000 : null
  )
  let [viewerData, setViewerData] = useState({}) // shape {imageLocs, index}
  let {onCenterSelectionChange, onBackwardClick, onForwardClick} = useNavigation({
    navigationControls,
    viewerData,
    setScrollToPageId,
    optionSet,
  })

  let onPageClick = null
  if (imageClick == 'zoomToHeight') {
    onPageClick = ({otid}) => setScrollToPageId({id: otid})
  }

  let {onViewerInstance, zoomIn, zoomOut} = useViewerControl()

  let renderCustomPaginations = ({page, availableHeight, availableWidth}) => {
    // log.x({availableHeight, availableWidth})
    let text = page.pbox?.title || page.caption
    return (
      <div
        style={{
          width: availableWidth,
          fontSize: '0.65em',
          //~ textOverflow: 'ellipsis',
          //~ overflow: 'hidden',
          //~ whiteSpace: 'nowrap',
        }}
      >
        {text}
      </div>
    )
  }
  let controls = null
  if (zoomControls || navigationControls || fullscreenToggle) {
    controls = (
      <ButtonGroup
        floated="right"
        style={{
          zIndex: '20',
          margin: '5px 0px',
          position: 'absolute',
          width: mobile ? 'calc(100% - 2.2em)' : 'calc(100% - 4.2em)',
        }}
      >
        {zoomControls && <ControlButton icon="minus" onClickFunc={zoomOut} />}
        {zoomControls && <ControlButton icon="plus" onClickFunc={zoomIn} />}
        {navigationControls && <ControlButton icon="arrow left" onClickFunc={onBackwardClick} />}
        {navigationControls && <ControlButton icon="arrow right" onClickFunc={onForwardClick} />}
        {fullscreenToggle && (
          <ControlButton
            icon={isFullscreen ? 'close' : 'expand'}
            onClickFunc={() => toggleFullscreen()}
          />
        )}
      </ButtonGroup>
    )
  }

  // XXX: this looks fishy
  let outerHeight = isString(height) && height.endsWith('%') ? height : undefined

  return (
    <>
      {controls}
      <div className={S.pageViewer} ref={ref} style={{height: outerHeight}}>
        <div ref={inviewRef} style={{width, height, display: 'flex'}}>
          <PageSetViewer
            otid={otid}
            otids={otids}
            optionSet={optionSet}
            options={options}
            scrollToPageId={scrollToPageId}
            flex
            loadOnVisible
            initialScrollTo={{id: otid}}
            renderCustomPaginations={renderCustomPaginations}
            onCenterSelectionChange={onCenterSelectionChange}
            onPageClick={onPageClick}
            onDataLoaded={setViewerData}
            debug={false}
            onViewerInstance={onViewerInstance}
          />
        </div>
        <div className={S.caption}>{useCaptionLink(caption, linkid, otid, null, true)}</div>
      </div>
    </>
  )
}
